import type { ContextKeyDescriptor } from '@mtm/context';

/**
 * Keys this app owns in the shared view context — the state behind the
 * `?ctx=<uuid>` query param (see `provideMtmContext` in `app.module.ts`).
 *
 * The short names are kept so the stored shape stays compact and the existing
 * call sites read the same. The task panel's keys (`tc`, `tid`, `tt`,
 * `subtaskId`) are deliberately NOT here: `@mtm/task-ui-angular` owns and
 * syncs them itself — the app only spreads its `TASK_CONTEXT_KEYS` into the
 * provider so they register before the first hydration.
 */
export const VIEW_CONTEXT_KEY = {
  listView: 'lv',
  task: 'ts',
  treeView: 'tv',
  comment: 'cm',
  compare: 'co',
} as const;

export type ViewContextKey = (typeof VIEW_CONTEXT_KEY)[keyof typeof VIEW_CONTEXT_KEY];

/**
 * On/off value per toggle. A missing key means "not applicable on this page"
 * (the control isn't rendered), which is different from an explicit `false`.
 */
export type ViewContextFlags = Partial<Record<ViewContextKey, boolean>>;

/**
 * Descriptors for `provideMtmContext({ keys })`. Derived as all-flags on
 * purpose: every key in `VIEW_CONTEXT_KEY` must be a boolean toggle. A future
 * string-valued key needs its own explicit `{ key, kind: 'value' }` entry —
 * registering it as a flag would make the SDK's sanitizer drop its values.
 */
export const WEBAPP_CONTEXT_KEYS: readonly ContextKeyDescriptor[] = Object.values(
  VIEW_CONTEXT_KEY
).map((key: ViewContextKey) => ({ key, kind: 'flag' as const }));

/**
 * The task panel's own context keys, mirrored from `@mtm/task-ui-angular`'s
 * `TASK_CONTEXT_KEY` (not exported in its typings). Only read to address a task
 * from outside the panel — the package registers and syncs them itself.
 */
export const TASK_PANEL_CONTEXT_KEY = {
  taskCreate: 'tc',
  taskId: 'tid',
  parentTaskId: 'ptid',
  boardId: 'bid',
  taskTab: 'tt',
  subtaskId: 'subtaskId',
} as const;
